/**
 * Utility functions for formatting and displaying data
 */

import { METRIC_LABELS } from './constants';

export class FormatUtil {
  /**
   * Format a score as X/5
   */
  static formatScore(score: number, maxScore: number = 5): string {
    return `${score}/${maxScore}`;
  }

  /**
   * Format date for display
   */
  static formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  }

  /**
   * Format date and time for display
   */
  static formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString();
  }

  /**
   * Get friendly metric label
   */
  static getMetricLabel(metricKey: string): string {
    return METRIC_LABELS[metricKey as keyof typeof METRIC_LABELS] || metricKey;
  }

  /**
   * Format location from city and country
   */
  static formatLocation(city?: string, country?: string): string {
    if (!city && !country) return '';
    if (!city) return country || '';
    if (!country) return city;
    return `${city}, ${country}`;
  }

  /**
   * Truncate text with ellipsis
   */
  static truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Format company size for display
   */
  static formatCompanySize(size: string): string {
    // Convert underscore notation to readable format
    return size.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Format sector for display
   */
  static formatSector(sector: string): string {
    return sector.replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Generate initials from name
   */
  static getInitials(name: string): string {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }

  /**
   * Format email for display (hide domain if too long)
   */
  static formatEmail(email: string, maxLength: number = 30): string {
    if (email.length <= maxLength) return email;
    const [username] = email.split('@');
    if (username.length + 5 <= maxLength) {
      return `${username}@...`;
    }
    return this.truncate(email, maxLength);
  }
}